import { Field, ImageField, Item, TextField } from '@sitecore-jss/sitecore-jss-nextjs';

import { isFilledIn } from './is-filled-in';

type SharedPropertyField = Field | ImageField | TextField | Item | Item[];

function getFieldWithInternalKey<T extends { value?: Record<string, unknown> }>(
  field: T,
  internalFieldKey: string
): T | undefined {
  const inner = field?.value?.[internalFieldKey];
  return inner && typeof inner === 'object' && Object.keys(inner).length > 0 ? field : undefined;
}

function isFieldFilledIn(
  field: SharedPropertyField | undefined,
  internalFieldKey?: string
): boolean {
  if (!field || !('value' in field)) return false;

  if (internalFieldKey)
    return !!getFieldWithInternalKey(
      field as { value?: Record<string, unknown> },
      internalFieldKey
    );

  return isFilledIn((field as Field).value);
}

/**
 * Retrieves a property field from the given fields object.
 * It first checks for a localized field, and if not found, it checks for a shared field.
 *
 * @param fields - The fields object containing the property fields.
 * @param fieldName - The name of the field to retrieve.
 * @param internalFieldKey - Optional key to check within the field's value object.
 * @returns The field if found and filled in, null if neither is filled in, or undefined if absent.
 */
export function getSharedProperty<T extends Record<K, SharedPropertyField>, K extends keyof T>(
  fields: T | undefined,
  fieldName: K,
  internalFieldKey?: string
): T[K] | null | undefined {
  if (!fields) return;
  const field = fields?.[fieldName];
  const sharedFieldName = `${fieldName as string}Shared` as keyof T;
  const sharedField = fields?.[sharedFieldName] as T[K];

  const isFieldAbsent = field === undefined && sharedField === undefined;
  if (isFieldAbsent) return undefined;

  const fieldValue = isFieldFilledIn(field, internalFieldKey);
  if (fieldValue) return field;

  const sharedFieldValue = isFieldFilledIn(sharedField, internalFieldKey);
  if (sharedFieldValue) return sharedField;

  return null;
}
